import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EvidenceBadge from "@/components/EvidenceBadge";
import { toast } from "sonner";
import { ArrowLeft, Warning, Pill, Flask, PencilSimple, Trash, Sparkle, CaretDown, CaretUp } from "@phosphor-icons/react";

export default function CompoundDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [c, setC] = useState(null);
    const [usage, setUsage] = useState(null);
    const [summary, setSummary] = useState(null);
    const [summaryUpdatedAt, setSummaryUpdatedAt] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [openEvidence, setOpenEvidence] = useState({});

    const load = () => api.get(`/compounds/${slug}`).then((r) => {
        setC(r.data);
        setSummary(r.data.evidence_summary || null);
        setSummaryUpdatedAt(r.data.evidence_summary_updated_at || null);
    }).catch(() => setC(null));

    useEffect(() => {
        load();
        api.get(`/compounds/${slug}/usage`).then((r) => setUsage(r.data)).catch(() => setUsage(null));
        // eslint-disable-next-line
    }, [slug]);

    const generateSummary = async (force = false) => {
        setSummaryLoading(true);
        try {
            const { data } = await api.post(`/compounds/${slug}/evidence-summary${force ? "?force=true" : ""}`);
            setSummary(data.summary);
            setSummaryUpdatedAt(data.updated_at);
            toast.success(data.cached ? "Resumen cargado de caché" : "Resumen generado por Claude");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error generando resumen");
        } finally { setSummaryLoading(false); }
    };

    const remove = async () => {
        const formulas = usage?.formulas_count || 0;
        const treatments = usage?.treatments_count || 0;
        let confirmMsg = `¿Eliminar el principio activo "${c.name}"? Esta acción NO se puede deshacer.`;
        if (formulas > 0 || treatments > 0) {
            confirmMsg = `⚠️ ATENCIÓN: "${c.name}" se utiliza en ${formulas} fórmula(s) y ${treatments} tratamiento(s).\n\nSi lo eliminas, esas referencias quedarán rotas pero el contenido histórico se conservará.\n\n¿Continuar igualmente?`;
        }
        if (!window.confirm(confirmMsg)) return;
        try {
            const force = formulas > 0 || treatments > 0 ? "?force=true" : "";
            await api.delete(`/compounds/${slug}${force}`);
            toast.success("Principio activo eliminado");
            navigate("/compuestos");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al eliminar");
        }
    };

    if (!c) return <div className="p-12"><span className="label-eyebrow">cargando…</span></div>;

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow={`Principio activo · ${c.category}`}
                title={c.name}
                lead={c.aka?.length ? `También conocido como: ${c.aka.join(", ")}` : null}
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/compuestos/${slug}/editar`} className="px-3.5 py-2 border hairline bg-white text-sm hover:bg-[var(--cream)] inline-flex items-center gap-2" data-testid="compound-detail-edit-button">
                            <PencilSimple size={14}/> Editar
                        </Link>
                        <button onClick={remove} className="px-3.5 py-2 border hairline bg-white text-sm hover:bg-[var(--cream)] text-[var(--terra-2)] inline-flex items-center gap-2" data-testid="compound-detail-delete-button">
                            <Trash size={14}/> Eliminar
                        </button>
                        <Link to={`/formulas/nuevo?preferred=${c.slug}`} className="btn-primary inline-flex items-center gap-2" data-testid="compound-detail-formulate-button">
                            <Flask size={16} weight="bold" /> Formular
                        </Link>
                    </div>
                }
            >
                <div className="mt-4 flex items-center gap-4 flex-wrap">
                    <Link to="/compuestos" className="text-sm text-[var(--ink-2)] inline-flex items-center gap-1 hover:text-[var(--ink)]"><ArrowLeft size={14}/> Volver al catálogo</Link>
                    {usage && (usage.formulas_count > 0 || usage.treatments_count > 0) && (
                        <span className="text-xs mono text-[var(--ink-3)]" data-testid="compound-usage">
                            En uso: {usage.formulas_count} fórmula{usage.formulas_count === 1 ? "" : "s"} · {usage.treatments_count} tratamiento{usage.treatments_count === 1 ? "" : "s"}
                        </span>
                    )}
                </div>
            </PageHeader>

            <div className="px-8 md:px-12 py-10 max-w-5xl grid lg:grid-cols-[2fr_1fr] gap-10">
                <div>
                    <Section title="Indicaciones">
                        <div className="flex flex-wrap gap-2">
                            {(c.indications || []).map((i) => (
                                <span key={i} className="citation-badge" data-testid={`indication-${i}`}>{i}</span>
                            ))}
                        </div>
                    </Section>

                    <Section title="Dosis por indicación" icon={Pill}>
                        <table className="data-table w-full surface">
                            <thead><tr><th>Indicación</th><th>Mínima</th><th>Típica</th><th>Máxima</th><th>Frecuencia</th></tr></thead>
                            <tbody>
                                {(c.dose_ranges || []).map((d, idx) => (
                                    <tr key={idx}>
                                        <td>{d.indication}</td>
                                        <td className="mono">{d.min_mg ?? "—"} {d.unit || "mg"}</td>
                                        <td className="mono"><strong>{d.typical_mg ?? "—"} {d.unit || "mg"}</strong></td>
                                        <td className="mono">{d.max_mg ?? "—"} {d.unit || "mg"}</td>
                                        <td>{d.frequency}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Section>

                    {/* AI evidence umbrella summary */}
                    <Section title="Resumen clínico de la evidencia" icon={Sparkle}>
                        {summary ? (
                            <div className="border hairline bg-white p-5" data-testid="compound-evidence-summary">
                                <p className="text-sm leading-relaxed whitespace-pre-line">{summary}</p>
                                <div className="flex items-center justify-between mt-4 pt-3 border-t hairline">
                                    <span className="text-xs text-[var(--ink-3)] mono">
                                        {summaryUpdatedAt ? `Actualizado: ${new Date(summaryUpdatedAt).toLocaleDateString("es-ES")}` : ""}
                                    </span>
                                    <button onClick={()=>generateSummary(true)} disabled={summaryLoading} className="text-xs text-[var(--sage)] hover:underline disabled:opacity-50" data-testid="compound-summary-regenerate">
                                        {summaryLoading ? "Regenerando…" : "Regenerar"}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="border hairline bg-[var(--cream)] p-5 flex items-center justify-between gap-4 flex-wrap">
                                <p className="text-sm text-[var(--ink-2)] max-w-md">
                                    Síntesis crítica de toda la evidencia indexada en 4-6 frases con citas [PMID]. Útil para escribir una recomendación rápida.
                                </p>
                                <button onClick={()=>generateSummary(false)} disabled={summaryLoading || (c.evidence || []).length === 0} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="compound-summary-generate">
                                    <Sparkle size={14} weight="bold"/> {summaryLoading ? "Generando…" : "Generar resumen IA"}
                                </button>
                            </div>
                        )}
                    </Section>

                    <Section title={`Evidencia científica · ${(c.evidence || []).length} referencias`} icon={Flask}>
                        {(c.evidence || []).length === 0 ? (
                            <p className="text-sm text-[var(--ink-3)]">Aún no se ha indexado evidencia para este compuesto. La ingesta automática se ejecuta cada 15 días.</p>
                        ) : (
                            <ul className="space-y-3">
                                {(c.evidence || []).map((e, idx) => {
                                    const key = `${e.source}-${e.external_id}-${idx}`;
                                    const open = !!openEvidence[key];
                                    return (
                                        <li key={key} className="border hairline bg-white" data-testid={`evidence-item-${e.external_id}`}>
                                            <div className="p-4">
                                                <div className="flex items-start justify-between gap-3">
                                                    <a href={e.url} target="_blank" rel="noreferrer" className="font-medium text-[var(--ink)] hover:text-[var(--sage)] leading-snug">{e.title}</a>
                                                    <EvidenceBadge source={e.source} externalId={e.external_id} url={e.url} />
                                                </div>
                                                <div className="text-xs text-[var(--ink-2)] mt-2 mono">
                                                    {e.journal} {e.year ? `· ${e.year}` : ""}
                                                </div>
                                                {e.abstract && (
                                                    <>
                                                        <p className={`text-sm text-[var(--ink-2)] mt-3 leading-relaxed ${open ? "" : "line-clamp-3"}`}>{e.abstract}</p>
                                                        <button
                                                            type="button"
                                                            onClick={() => setOpenEvidence((p) => ({ ...p, [key]: !open }))}
                                                            className="mt-2 text-xs text-[var(--sage)] inline-flex items-center gap-1 hover:underline"
                                                            data-testid={`evidence-toggle-${e.external_id}`}
                                                        >
                                                            {open ? <><CaretUp size={12}/> Plegar resumen</> : <><CaretDown size={12}/> Leer resumen completo</>}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </Section>
                </div>

                <aside>
                    <div className="border hairline bg-white p-5 mb-5">
                        <div className="label-eyebrow mb-3">Límite superior (UL)</div>
                        <div className="font-display text-3xl tracking-tight">{c.upper_limit_mg ?? "n/a"} <span className="text-sm text-[var(--ink-3)] mono">mg/día</span></div>
                        <p className="text-xs text-[var(--ink-2)] mt-2">Referencia toxicológica para adultos sanos.</p>
                    </div>

                    <div className="border hairline bg-[var(--cream)] p-5 mb-5">
                        <div className="flex items-center gap-2 label-eyebrow mb-3 text-[var(--terra-2)]">
                            <Warning size={14} weight="duotone" /> Contraindicaciones
                        </div>
                        {(c.contraindications || []).length === 0 ? (
                            <p className="text-sm text-[var(--ink-2)]">No documentadas en este compuesto.</p>
                        ) : (
                            <ul className="text-sm space-y-1.5 text-[var(--ink)]">
                                {(c.contraindications || []).map((x, i) => <li key={i}>• {x}</li>)}
                            </ul>
                        )}
                    </div>

                    {(c.interactions || []).length > 0 && (
                        <div className="border hairline bg-white p-5">
                            <div className="label-eyebrow mb-3">Interacciones</div>
                            <ul className="text-sm space-y-1.5 text-[var(--ink-2)]">
                                {c.interactions.map((x, i) => <li key={i}>• {x}</li>)}
                            </ul>
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <section className="mb-10">
            <div className="flex items-center gap-2 label-eyebrow mb-4">
                {Icon && <Icon size={14} weight="duotone" />}
                <span>{title}</span>
            </div>
            {children}
        </section>
    );
}
